import React, {useEffect, useState} from 'react';
import {Checkbox, Col, Collapse, DatePicker, Form, Input, message, Row, Skeleton} from 'antd';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from '../../../../hooks/useTranslation';
import {useDateFormat} from '../../../../hooks/useDateFormat';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import SelectOption from '../../../../components/combobox/SelectOption';
import SelectStatus from "../../../../components/combobox/SelectStatus";
import {API_PARAMS, ISSUE_TYPES} from "../../../../constants/constants";
import SelectProcess from '../../../../components/combobox/SelectProcess';
import SelectArea from '../../../../components/combobox/SelectArea';
import SelectUser from '../../../../components/combobox/SelectUser';
import SelectYesNo from '../../../../components/combobox/SelectYesNo';
import CurrencyInput from '../../../../components/number/CurrencyInput';
import {Attachment, useAttachments} from '../../../../hooks/useAttachments';
import {UploadFile} from 'antd/lib';
import Attachments from '../../../../components/attachment/Attachments';
import MetaTable from '../../../../components/common/MetaTable';
import {useCreateIssue, useIssueDetail, useUpdateIssue} from '../../../../hooks/issues/useIssues';
import {useProcess} from '../../../../components/combobox/hooks/useProcess';
import {useArea} from '../../../../components/combobox/hooks/useArea';
import {convertToUTCTimestamp, convertUTCTimestampToLocal} from "../../../../utils/dateFormatter";
import {ApartmentOutlined, DashboardOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {extractErrorMessage} from '../../../../utils/apiError';

dayjs.extend(customParseFormat);

const {TextArea} = Input;

export interface IssueFormActions {
  submit: () => void;
}

interface IssueFormProps {
  issueId?: string;
  onRegisterActions?: (actions: IssueFormActions) => void;
  onSavingChange?: (saving: boolean) => void;
}

const IssueForm: React.FC<IssueFormProps> = ({issueId, onRegisterActions, onSavingChange}) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [attachments, setAttachments] = useState<UploadFile<Attachment>[] | undefined>([]);
    const [seededIssueId, setSeededIssueId] = useState<string | undefined>(undefined);
    const [fields, setFields] = useState<any[]>([]);

    const {t} = useTranslation();
    const {dateTimeFormat} = useDateFormat();
    const navigate = useNavigate();

    const [messageApi, contextHolder] = message.useMessage();

    // Check if we're editing an existing issue or creating a new one
    const isEditMode = !!issueId && issueId !== 'new';

    // Use the useIssueDetail hook to fetch issue data - only when editing
    const {data: issueData, isLoading, error} = useIssueDetail(isEditMode ? issueId : undefined);

    const updateIssueMutation = useUpdateIssue();
    const createIssueMutation = useCreateIssue();

    // Prefetch processes only when editing an existing issue
    const {
      data: prefetchedProcesses = [],
      isLoading: isLoadingProcesses
    } = useProcess({node: API_PARAMS.ZERO, depth: API_PARAMS.ZERO}, {enabled: isEditMode && !!issueData});

    // Prefetch areas only when editing an existing issue
    const {
      data: prefetchedAreas = [],
      isLoading: isLoadingAreas
    } = useArea({node: API_PARAMS.ROOT, depth: API_PARAMS.ZERO}, {enabled: isEditMode && !!issueData});

    // Load attachments only when editing an existing issue
    const {data, isLoading: isLoadingAttachments} = useAttachments({
      uid: isEditMode ? issueId : undefined,
      module: API_PARAMS.IMPACT.ISSUES_ENTITY,
      subModule: '',
      uuidSubEntity: ''
    });

    // Map and populate form fields when issue data is loaded
    useEffect(() => {
      // Wait until issueData is available and prefetched selects are loaded
      if (isEditMode && issueData && !isLoadingProcesses && !isLoadingAreas) {

        form.setFieldsValue({
          code: issueData.codeIncident,
          title: issueData.title,
          origin: issueData.origin?.id,
          effect: issueData.effect?.id,
          description: issueData.description,
          comments: issueData.comments,
          status: issueData.status?.id,
          dateReport: convertUTCTimestampToLocal(issueData.dateReport),
          dateInit: convertUTCTimestampToLocal(issueData.dateInit),
          dateEnd: convertUTCTimestampToLocal(issueData.dateEnd),
          dateDiscovery: convertUTCTimestampToLocal(issueData.dateDiscovery),
          dateMitigation: convertUTCTimestampToLocal(issueData.dateMitigation),
          area: issueData.area || [],
          resolvingArea: issueData.resolvingArea ? issueData.resolvingArea.id : null,
          channelAffected: issueData.channelAffected?.map((c: any) => c.id) || [],
          channelReport: issueData.channelReport?.map((c: any) => c.id) || [],
          process: issueData.process || [],
          issueActionPlan: issueData.issueActionPlan?.id,
          rootCause: issueData.rootCause,
          typeIncident: issueData.typeIncident,
          resourceAffected: issueData.resourceAffected?.map((r: any) => r.id) || [],
          typeAssetAffected: issueData.typeAssetAffected?.map((a: any) => a.id) || [],
          numberTicket: issueData.numberTicket,
          hasTicket: issueData.hasTicket || 'N',
          typeThreat: issueData.typeThreat?.id,
          typeMaterialization: issueData.typeMaterialization?.id,
          typeVulnerability: issueData.typeVulnerability?.id,
          numberCustomerAffected: issueData.numberCustomerAffected || 0,
          lossCustomer: issueData.lossCustomer || 0,
          costMitigation: issueData.costMitigation || 0,
          supplier: issueData.hasSupplier || false,
          reportingUser: issueData.reportingUser,
        });
      }
    }, [issueData, form, isLoadingProcesses, isLoadingAreas, isEditMode]);

    if (data && seededIssueId !== issueId) {
      setSeededIssueId(issueId);
      setAttachments(data);
    }

    // Show an error message if issue data fails to load
    useEffect(() => {
      if (error) {
        messageApi.error('Failed to load issue data');
      }
    }, [error, messageApi]);

    // Expose the submit action to the page header
    useEffect(() => {
      onRegisterActions?.({submit: () => form.submit()});
    }, [form, onRegisterActions]);

    // Report saving state so the header button can show a loading spinner
    useEffect(() => {
      onSavingChange?.(saving);
    }, [saving, onSavingChange]);

    const handleSave = async () => {
      setSaving(true);
      try {
        if (isEditMode) {
          // Update existing issue
          await updateIssueMutation.mutateAsync({id: issueId, data: fields});

          void messageApi.success(t('issues.messages.updateSuccess'));
          setFields([]);
        } else {
          // Create a new issue
          const response = await createIssueMutation.mutateAsync(fields);

          void messageApi.success(t('issues.messages.createSuccess'));
          // Navigate to the new issue: UUID in the path, numeric correlative as a
          // query param so the assessment panel has the id it keys on.
          const correlative = response.data.correlative ?? response.data.id;
          const suffix = correlative != null ? `?correlative=${correlative}` : '';
          navigate(`/detail/${response.data.uid}${suffix}`);
        }
      } catch (err) {
        void messageApi.error(extractErrorMessage(err, isEditMode ? 'Failed to save issue' : 'Failed to create issue'));
      } finally {
        setSaving(false);
      }
    };

    const handleOnChangeForm = (changedField: any) => {
      Object.entries(changedField).forEach(([fieldName, fieldValue]) => {

        const isDateValue = fieldValue && typeof fieldValue === 'object' && typeof fieldValue.valueOf === 'function' && !Array.isArray(fieldValue);

        // Convert date to UTC timestamp if it's a date field
        const processedValue = isDateValue ? convertToUTCTimestamp(fieldValue) : fieldValue;

        const newField = {
          fieldName: fieldName,
          newValue: processedValue,
          multiSelect: Array.isArray(fieldValue),
        };

        // Update fields state - replace it if the field already exists, otherwise add
        setFields(prev => {
          // Find if this field already exists by fieldName
          const existingIndex = prev.findIndex(f => f.fieldName === fieldName);

          if (existingIndex !== -1) {
            // Field exists - replace it with a new value
            const updated = [...prev];
            updated[existingIndex] = newField;
            return updated;
          } else {
            // Field doesn't exist - add it
            return [...prev, newField];
          }
        });
      });
    }

    // Show skeleton when loading an existing issue or when saving
    const showSkeleton = (isEditMode && isLoading);

    const basicSection = (
      <>
        <Row>
          <Col span={24}>
            <Form.Item
              name="title"
              label={t('issues.detail.form.title.name')}
              rules={[
                {required: true, message: t('issues.detail.form.title.validation.required')},
                {min: 10, message: t('issues.detail.form.title.validation.min')},
                {max: 1000, message: t('issues.detail.form.title.validation.max')}
              ]}
            >
              <TextArea
                placeholder={t('issues.detail.form.title.placeHolder')}
                className={"issue-textarea-scrollbar"}
                autoSize={{maxRows: 2}}
                maxLength={1000}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="description"
              label={t('issues.detail.form.description.name')}
              className={"issue-textarea-scrollbar"}
              rules={[
                {required: true, message: t('issues.detail.form.description.validation.required')},
                {min: 10, message: t('issues.detail.form.description.validation.min')},
                {max: 3000, message: t('issues.detail.form.description.validation.max')}
              ]}
            >
              <TextArea
                placeholder={t('issues.detail.form.description.placeHolder')}
                className={"issue-textarea-scrollbar"}
                autoSize={{minRows: 4, maxRows: 10}}
                maxLength={3000}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="dateReport"
              label={t('issues.detail.form.dateReport.name')}
              rules={[{required: true, message: t('issues.detail.form.dateReport.validation.required')}]}
            >
              <DatePicker format={dateTimeFormat} style={{width: '100%'}}
                          placeholder={t('issues.detail.form.dateReport.placeHolder')} showTime/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="dateInit" label={t('issues.detail.form.dateInit.name')}>
              <DatePicker format={dateTimeFormat} style={{width: '100%'}}
                          placeholder={t('issues.detail.form.dateInit.placeHolder')} showTime/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="dateDiscovery" label={t('issues.detail.form.dateDiscovery.name')}>
              <DatePicker format={dateTimeFormat} style={{width: '100%'}}
                          placeholder={t('issues.detail.form.dateDiscovery.placeHolder')} showTime/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="dateMitigation" label={t('issues.detail.form.dateMitigation.name')}>
              <DatePicker format={dateTimeFormat} style={{width: '100%'}}
                          placeholder={t('issues.detail.form.dateMitigation.placeHolder')} showTime/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col span={24} sm={12}>
            <Form.Item name="dateEnd" label={t('issues.detail.form.dateEnd.name')}>
              <DatePicker format={dateTimeFormat} style={{width: '100%'}}
                          placeholder={t('issues.detail.form.dateEnd.placeHolder')} showTime/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="issueActionPlan" label={t('issues.detail.form.issueActionPlan.name')}>
              <SelectOption
                optionType="ISSUE_ACTION_PLAN"
                isFilterable={false}
                optionKey={'issueActionPlan'}
                placeholder={t('issues.detail.form.issueActionPlan.placeHolder')}
                initialOptions={issueData?.issueActionPlan ? [issueData.issueActionPlan] : []}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="origin"
              label={t('issues.detail.form.origin.name')}
              rules={[{required: true, message: t('issues.detail.form.origin.validation.required')}]}
            >
              <SelectOption
                optionType="ISSUE_ORIGIN"
                allowClear
                isFilterable={true}
                optionKey={'origin'}
                placeholder={t('issues.detail.form.origin.placeHolder')}
                initialOptions={issueData?.origin ? [issueData.origin] : []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="effect"
              label={t('issues.detail.form.effect.name')}
              rules={[{required: true, message: t('issues.detail.form.effect.validation.required')}]}
            >
              <SelectOption
                optionType="ISSUE_EFFECT"
                isFilterable={true}
                optionKey={'effect'}
                placeholder={t('issues.detail.form.effect.placeHolder')}
                initialOptions={issueData?.effect ? [issueData.effect] : []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="supplier" valuePropName="checked" className="issue-form-checkbox-field">
              <Checkbox>{t('issues.detail.form.supplier')}</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </>
    );

    const classificationSection = (
      <>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="process" label={t('issues.detail.form.process.name')}>
              <SelectProcess
                placeholder={t('issues.detail.form.process.placeHolder')}
                initialTreeData={prefetchedProcesses}
                onChange={() => {
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="area" label={t('issues.detail.form.area.name')}>
              <SelectArea multiple={true} treeCheckable={true}
                          placeholder={t('issues.detail.form.area.placeHolder')}
                          initialTreeData={prefetchedAreas}/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="channelAffected" label={t('issues.detail.form.channelAffected.name')}>
              <SelectOption
                optionType="ISSUE_CHANNEL_AFFECTED"
                isFilterable={false}
                optionKey={'channelAffected'}
                placeholder={t('issues.detail.form.channelAffected.placeHolder')}
                mode={"multiple"}
                initialOptions={issueData?.channelAffected || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="resourceAffected" label={t('issues.detail.form.resourceAffected.name')}>
              <SelectOption
                optionType="ISSUE_RESOURCE_AFFECTED"
                isFilterable={false}
                optionKey={'resourceAffected'}
                placeholder={t('issues.detail.form.resourceAffected.placeHolder')}
                mode="multiple"
                initialOptions={issueData?.resourceAffected || []}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="channelReport" label={t('issues.detail.form.channelReport.name')}>
              <SelectOption
                optionType="ISSUE_CHANNEL_REPORT"
                isFilterable={false}
                optionKey={'channelReport'}
                placeholder={t('issues.detail.form.channelReport.placeHolder')}
                mode={"multiple"}
                initialOptions={issueData?.channelReport || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="reportingUser" label={t('issues.detail.form.reportingUser.name')}>
              <SelectUser placeholder={t('issues.detail.form.reportingUser.placeHolder')} onChange={() => {
              }}/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="resolvingArea" label={t('issues.detail.form.resolvingArea.name')}>
              <SelectArea multiple={false} treeCheckable={false}
                          placeholder={t('issues.detail.form.resolvingArea.placeHolder')}
                          initialTreeData={prefetchedAreas}/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="hasTicket" label={t('issues.detail.form.hasTicket.name')}>
              <SelectYesNo returnType={"string"} stringFormat={"S/N"}/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="typeThreat" label={t('issues.detail.form.typeThreat.name')}>
              <SelectOption
                optionType="ISSUE_THREAT"
                isFilterable={false}
                optionKey={'typeThreat'}
                placeholder={t('issues.detail.form.typeThreat.placeHolder')}
                initialOptions={issueData?.typeThreat ? [issueData.typeThreat] : []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="typeAssetAffected" label={t('issues.detail.form.typeAssetAffected.name')}>
              <SelectOption
                optionType="ISSUE_ASSET_INFORMATION"
                isFilterable={false}
                optionKey={'typeAssetAffected'}
                placeholder={t('issues.detail.form.typeAssetAffected.placeHolder')}
                mode={"multiple"}
                initialOptions={issueData?.typeAssetAffected || []}
              />
            </Form.Item>
          </Col>
        </Row>
      </>
    );

    const impactSection = (
      <>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="numberCustomerAffected" label={t('issues.detail.form.numberCustomerAffected.name')}>
              <CurrencyInput style={{width: '100%'}} decimalPlaces={0} currencySymbol={''}/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="lossCustomer" label={t('issues.detail.form.lossCustomer.name')}>
              <CurrencyInput style={{width: '100%'}}/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="costMitigation" label={t('issues.detail.form.costMitigation.name')}>
              <CurrencyInput style={{width: '100%'}}/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24}>
            <Form.Item
              name="rootCause"
              label={t('issues.detail.form.rootCause.name')}
              rules={[{max: 3000, message: t('issues.detail.form.description.validation.max')}]}
            >
              <TextArea placeholder={t('issues.detail.form.rootCause.placeHolder')}
                        autoSize={{maxRows: 4}} maxLength={600} showCount/>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="comments"
              label={t('issues.detail.form.comments.name')}
              rules={[
                {required: false, message: t('issues.detail.form.comments.validation.required')},
                {min: 10, message: t('issues.detail.form.comments.validation.min')},
                {max: 1000, message: t('issues.detail.form.comments.validation.max')}
              ]}
            >
              <TextArea placeholder={t('issues.detail.form.comments.placeHolder')}
                        autoSize={{maxRows: 4}} maxLength={1000} showCount/>
            </Form.Item>
          </Col>
        </Row>
      </>
    );

    const reportedOnText = issueData?.dateReport
      ? dayjs(issueData.dateReport).format(dateTimeFormat)
      : '—';
    const reporter = issueData?.reportingUser;
    const reporterText = typeof reporter === 'string'
      ? reporter
      : (reporter?.name || reporter?.description || reporter?.id || '—');

    return (
      <>
        {contextHolder}
        <Skeleton loading={showSkeleton} round={true}>
          <Form
            form={form} className={'issue-form'}
            layout="vertical"
            onFinish={handleSave}
            scrollToFirstError
            requiredMark={false}
            onValuesChange={handleOnChangeForm}
          >
            <Row gutter={16} className={'issue-form-container'}>
              <Col xs={24} sm={16} lg={16} xxl={16}>
                <Collapse
                  defaultActiveKey={['basic', 'classification', 'impact']}
                  bordered={false}
                  expandIconPlacement="end"
                  className="issue-form-sections"
                  items={[
                    {
                      key: 'basic',
                      label: (
                        <span className="issue-panel-header">
                          <InfoCircleOutlined/>
                          {t('issues.detail.form.sections.basic')}
                        </span>
                      ),
                      children: basicSection
                    },
                    {
                      key: 'classification',
                      label: (
                        <span className="issue-panel-header">
                          <ApartmentOutlined/>
                          {t('issues.detail.form.sections.classification')}
                        </span>
                      ),
                      children: classificationSection
                    },
                    {
                      key: 'impact',
                      label: (
                        <span className="issue-panel-header">
                          <DashboardOutlined/>
                          {t('issues.detail.form.sections.additional')}
                        </span>
                      ),
                      children: impactSection
                    }
                  ]}
                />
              </Col>
              <Col xs={24} sm={8} lg={8} xxl={8}>
                <div className="issue-summary-card">
                  <MetaTable
                    rows={[
                      {
                        key: 'status',
                        label: t('issues.detail.form.summary.status'),
                        value: (
                          <Form.Item
                            name="status"
                            noStyle
                            rules={[{required: true, message: t('issues.detail.form.status.validation.required')}]}
                          >
                            <SelectStatus
                              type={ISSUE_TYPES.OPERATIONAL_RISK}
                              placeholder={t('issues.detail.form.status.placeHolder')}
                              initialOptions={issueData?.status ? [issueData.status] : []}
                              variant={"borderless"}
                            />
                          </Form.Item>
                        ),
                      },
                      {
                        key: 'reportedBy',
                        label: t('issues.detail.form.summary.reportedBy'),
                        value: reporterText,
                      },
                      {
                        key: 'reportedOn',
                        label: t('issues.detail.form.summary.reportedOn'),
                        value: reportedOnText,
                      },
                    ]}
                  />
                  <div className="issue-attachments">
                    <div className="issue-attachments-header">
                      <span className="issue-attachments-title">
                        {t('issues.detail.form.summary.attachments')}
                      </span>
                      <span className="issue-attachments-count">
                        {`${attachments?.length || 0} ${t('issues.detail.form.summary.fileCountSuffix')}`}
                      </span>
                    </div>
                    <Attachments
                      idEntity={issueId}
                      attachments={attachments}
                      setAttachments={setAttachments}
                      isLoading={isLoadingAttachments}
                    />
                    <div className="issue-attachments-hint">
                      {t('issues.detail.form.summary.maxFiles')}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </Skeleton>
      </>
    );
  }
;

export default IssueForm;

